import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ArrowDown from "../../../../assets/media/icons/ArrowDown";
import Close from "../../../../assets/media/svgs/Close";
import ZostelLogo from "../../../../components/svg/ZostelLogo";
import Button from "../../../../components/ui/Button";
import { gtag } from "../../../../utils/track";
import AppDownloadSection from "../Navigation/components/AppDownloadSection";

interface Region {
  name: string;
  type: number;
  slug: string;
}

interface NavItem {
  id: number;
  title: string;
  image: string;
  navTitle: string;
  link: string;
  icon: React.ReactNode;
  items?: { label: string; to: string; region?: Region[] }[];
}

interface NavigationProps {
  navItems: NavItem[];
  isMobile: boolean;
  mobileMenuOpen: boolean;
  activeDropdown: number | null;
  handleMouseEnter: (id: number) => void;
  handleMouseLeave: () => void;
  toggleMobileMenu: () => void;
  toggleMobileDropdown: (id: number) => void;
}

// Desktop Navigation Component
const DesktopNavigation: React.FC<{
  navItems: NavItem[];
  activeDropdown: number | null;
  handleMouseEnter: (id: number) => void;
  handleMouseLeave: () => void;
}> = ({ navItems, activeDropdown, handleMouseEnter, handleMouseLeave }) => {
  const handleClick = (e: React.MouseEvent, itemId: number) => {
    e.preventDefault();
    if (activeDropdown === itemId) {
      handleMouseLeave();
    } else {
      handleMouseEnter(itemId);
    }
  };

  const handlePlanYourTripToIndia = () => {
    gtag("event", "usf_tertiary_cta_click", {
      button_text: "Plan Your Trip To India",
      button_position: "top_nav_bar",
    });
  };
  return (
    <div className=" hidden md:block pb-2">
      <div className="px-6 sm:px-28 ">
        <div className="flex justify-between items-center">
          {/* Navigation Items */}
          <div className="flex space-x-2">
            {navItems.map((item) => (
              <div
                key={item.id}
                className="relative group nav-item"
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => handleClick(e, item.id)}
                role="button"
                tabIndex={0}
              >
                <div className="text-primary  hover:bg-light-grey rounded-full px-3 py-2 text-sm transition-all duration-300 relative nav-link flex items-center">
                  <span>{item.icon}</span>
                  <span className="text-sm ml-1">{item.navTitle}</span>
                  <ArrowDown
                    className={`w-4 h-4 ml-1 zostel-nav-arrow-transition ${
                      activeDropdown === item.id
                        ? "zostel-nav-arrow-rotated"
                        : ""
                    }`}
                  />
                </div>

                {activeDropdown === item.id && item.items && (
                  <DropdownMenu item={item} />
                )}
              </div>
            ))}
            <Link
              to={"/destinations"}
              className="text-primary  hover:bg-light-grey rounded-full px-3 py-2 text-sm transition-all duration-300 relative nav-link flex items-center"
            >
              <span role="img" aria-label="Destinations" className="mr-1">
                🗺️
              </span>

              <span className="text-sm ml-1">Destinations</span>
            </Link>
          </div>

          {/* Join Us Links */}
          {window.location.pathname === "/us" ? (
            <a
              href="https://zostel.com/"
              onClick={handlePlanYourTripToIndia}
              className="flex items-center"
            >
              <span role="img" aria-label="Plan a Trip to India">
                ✈️
              </span>
              <span className="text-sm ml-1">Plan a Trip to India</span>
            </a>
          ) : (
            <div className="ml-auto flex items-center gap-4">
              <Link
                to="/career"
                className="text-primary join-us-link hover:bg-light-grey rounded-full px-3 py-2 text-sm transition-all duration-300 relative nav-link flex items-center"
              >
                <span
                  className="text-base"
                  role="img"
                  aria-label="Work with us"
                >
                  🤝
                </span>
                <span className="text-sm ml-1">Work with us</span>
              </Link>
              <Link
                to="/franchise"
                className="text-primary join-us-link hover:bg-light-grey rounded-full px-3 py-2 text-sm transition-all duration-300 relative nav-link flex items-center"
              >
                <span
                  className="text-base "
                  role="img"
                  aria-label="Open Franchise"
                >
                  💼
                </span>
                <span className="text-sm ml-1">Open Franchise</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dropdown Menu Component
const DropdownMenu: React.FC<{ item: NavItem }> = ({ item }) => {
  const standardRegions = [
    "North India",
    "South India",
    "East India",
    "West India",
  ];

  const displayNames: Record<string, string> = {
    "North India": "North",
    "South India": "South",
    "East India": "East",
    "West India": "West",
  };

  const [selectedRegion, setSelectedRegion] = useState<string>(
    standardRegions[0]
  );

  // Check if any items have region data
  const hasRegions = useMemo(() => {
    return item.items?.some((item) => item.region?.some((r) => r.type === 2));
  }, [item.items]);

  // Filter and display items based on regions
  const filteredItems = useMemo(() => {
    if (!hasRegions) return item.items;
    return item.items?.filter((link) =>
      link.region?.some((r) => r.type === 2 && r.name === selectedRegion)
    );
  }, [item.items, selectedRegion, hasRegions]);

  // Calculate number of columns based on filtered items length
  const numberOfColumns = useMemo(() => {
    const itemsLength = filteredItems?.length || 0;
    if (itemsLength > 8) {
      return Math.min(4, Math.ceil(itemsLength / 8));
    }
    return 1;
  }, [filteredItems]);

  const handleNavItemClick = (identifier: string) => {
    gtag("event", "usf_product_browsing", {
      button_text: identifier,
      button_position: "top_nav_bar",
    });
  };

  return (
    <div
      style={{ paddingTop: "10px" }}
      className="absolute top-full left-0 z-10 cursor-default"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-2xl shadow-md">
        <div className="p-6 flex gap-8">
          {/* Left Section */}
          <div className="w-48 flex flex-col">
            <div className="rounded-2xl overflow-hidden mb-2">
              <img
                src={item.image}
                alt="Zostel"
                className="w-full h-32 object-cover"
              />
            </div>
            <p className="text-secondary mb-4 text-xs">{item.title}</p>
            <Link
              to={`${item.link}`}
              className="inline-flex w-20 items-center justify-center bg-primary rounded-full text-white text-xs font-medium px-3 py-2 hover:bg-orange cursor-pointer"
            >
              <span>See All</span>
            </Link>
          </div>

          {/* Right Section */}
          <div className="flex-1" style={{ minWidth: "400px" }}>
            {/* Region Filter Buttons */}
            {hasRegions && (
              <div className="mb-6">
                <div className="flex gap-2">
                  {standardRegions.map((region) => (
                    <button
                      key={region}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedRegion(region);
                      }}
                      className={`text-xs px-4 py-2 rounded-full focus:outline-none border whitespace-nowrap cursor-pointer ${
                        selectedRegion === region
                          ? "border-primary text-primary"
                          : "border-stroke-primary text-primary"
                      }`}
                    >
                      {displayNames[region]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {filteredItems && filteredItems.length > 0 ? (
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `repeat(${numberOfColumns}, minmax(180px, 1fr))`,
                  gap: "24px",
                  width: "100%",
                }}
              >
                {Array.from({ length: numberOfColumns }).map((_, colIndex) => (
                  <div key={colIndex} className="flex flex-col space-y-3">
                    {filteredItems
                      ?.slice(colIndex * 8, (colIndex + 1) * 8)
                      .map((link, index) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={handleNavItemClick.bind(null, link.label)}
                          className="text-primary hover:text-orange text-xs truncate cursor-pointer"
                          style={
                            {
                              "--delay": `${(index + colIndex) * 0.05}s`,
                            } as React.CSSProperties
                          }
                        >
                          {link.label}
                        </Link>
                      ))}
                  </div>
                ))}
              </div>
            ) : (
              item.id !== 4 && (
                <div className="text-center py-6">
                  <p className="text-secondary font-medium">Coming Soon</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Navigation Component
const MobileNavigation: React.FC<{
  navItems: NavItem[];
  mobileMenuOpen: boolean;
  activeDropdown: number | null;
  toggleMobileMenu: () => void;
  toggleMobileDropdown: (id: number) => void;
}> = ({
  navItems,
  mobileMenuOpen,
  activeDropdown,
  toggleMobileMenu,
  toggleMobileDropdown,
}) => {
  const handleStartYourOwnZostel = () => {
    gtag("event", "usf_main_cta_click", {
      button_text: "Start your own Zostel",
      button_position: "top_nav_bar",
    });
  };
  return (
    <div
      className={`fixed inset-0 z-50 bg-white flex flex-col 
        ${
          mobileMenuOpen
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-full pointer-events-none"
        }`}
    >
      {/* Fixed Header with Logo and Close Button */}
      <div
        className={`sticky top-0 z-10 bg-white border-b border-stroke-primary px-6 py-6 flex justify-between items-center 
        ${
          mobileMenuOpen
            ? "translate-x-0 opacity-100"
            : "translate-x-8 opacity-0"
        }`}
      >
        <Link to="/">
          <ZostelLogo
            className="h-8 w-36"
            textClassName="fill-current text-dark"
          />
        </Link>
        <button onClick={toggleMobileMenu} className="focus:outline-none">
          <Close className="w-6 h-6" />
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: "180px" }}
      >
        <div
          className={`px-6
          ${
            mobileMenuOpen
              ? "translate-x-0 opacity-100"
              : "translate-x-8 opacity-0"
          }`}
        >
          {/* Navigation Items */}
          <div className="space-y-0">
            {navItems.map((item, index) => (
              <div
                key={item.id}
                className={`
                ${
                  mobileMenuOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-10 opacity-0"
                }
              `}
              >
                <MobileNavItem
                  item={item}
                  activeDropdown={activeDropdown}
                  toggleMobileDropdown={toggleMobileDropdown}
                  toggleMobileMenu={toggleMobileMenu}
                />
              </div>
            ))}
          </div>

          <hr className="horizontal-divider my-3" />

          {/* Additional Links */}
          <div className="space-y-6 mt-4">
            {[
              { to: "/destinations", icon: "🗺️", label: "Destinations" },
              { to: "/career", icon: "🤝", label: "Work with us" },
              { to: "/franchise", icon: "💼", label: "Open Franchise" },
            ].map(({ to, icon, label }, index) => (
              <Link
                key={to}
                to={to}
                onClick={toggleMobileMenu}
                className={`flex items-center gap-2
                ${
                  mobileMenuOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-10 opacity-0"
                }`}
              >
                <span className="text-base" role="img" aria-label={label}>
                  {icon}
                </span>
                <span className="text-base">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed Download App Card */}
      <div
        className={`fixed md:hidden z-10 bottom-0 left-0 right-0 bg-white 
        px-2 sm:px-4 pb-6 sm:pb-10 shadow-lg
        ${
          mobileMenuOpen
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0"
        }`}
      >
        <div className="px-6">
          {window.location.pathname === "/us" ? (
            <Button onClick={handleStartYourOwnZostel} className="w-full">
              Start your own Zostel
            </Button>
          ) : (
            <AppDownloadSection />
          )}
        </div>
      </div>
    </div>
  );
};

// Mobile Navigation Item Component
const MobileNavItem: React.FC<{
  item: NavItem;
  activeDropdown: number | null;
  toggleMobileDropdown: (id: number) => void;
  toggleMobileMenu: () => void;
}> = ({ item, activeDropdown, toggleMobileDropdown, toggleMobileMenu }) => {
  // Limit items to 16
  const limitedItems = item.items ? item.items.slice(0, 16) : [];
  const isActive = activeDropdown === item.id;

  const handleNavItemClick = (identifier: string) => {
    gtag("event", "usf_product_browsing", {
      button_text: identifier,
      button_position: "top_nav_bar",
    });
    toggleMobileMenu();
  };

  return (
    <div className="py-3">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => toggleMobileDropdown(item.id)}
      >
        <div className="text-base flex items-center">
          <span className="mr-3">{item.icon}</span>
          {item.navTitle}
        </div>

        <ArrowDown
          className={`w-4 h-4 ml-1 zostel-nav-arrow-transition ${
            isActive ? "zostel-nav-arrow-rotated" : ""
          }`}
        />
      </div>
      {isActive && item.items && (
        <div className="mt-2 ml-4 mobile-dropdown max-h-[50vh] overflow-y-auto pb-4">
          <div className="grid grid-cols-1 gap-2">
            {limitedItems.map((subItem, index) => (
              <div
                key={index}
                className="mb-2"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Link
                  to={subItem.to}
                  className="text-primary text-sm block py-1"
                  onClick={handleNavItemClick.bind(null, subItem.label)}
                >
                  {subItem.label}
                </Link>
              </div>
            ))}
          </div>
          <Link
            to={`${item.link}`}
            className="flex w-24 justify-center mt-4 items-center rounded-full text-white text-xs font-medium px-3 py-2 bg-primary"
            onClick={toggleMobileMenu}
          >
            <span>See All</span>
          </Link>
        </div>
      )}
    </div>
  );
};

// Main Navigation Component
const Navigation: React.FC<NavigationProps> = ({
  navItems,
  isMobile,
  mobileMenuOpen,
  activeDropdown,
  handleMouseEnter,
  handleMouseLeave,
  toggleMobileMenu,
  toggleMobileDropdown,
}) => {
  return (
    <>
      {/* Desktop Navigation */}
      <DesktopNavigation
        navItems={navItems}
        activeDropdown={activeDropdown}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
      />

      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNavigation
          navItems={navItems}
          mobileMenuOpen={mobileMenuOpen}
          activeDropdown={activeDropdown}
          toggleMobileMenu={toggleMobileMenu}
          toggleMobileDropdown={toggleMobileDropdown}
        />
      )}
    </>
  );
};

export default Navigation;
